// Phase 8 Plan 03 Task 1 — per-template walker for the `branch-variant` template.
//
// Pure TS — no React, no DOM. Server-friendly: Phase 10 AI prompt-gen + Phase 13 SSR
// re-import from here. Hand-written per RESEARCH §"Pattern 2 Option A".
//
// configSchema source mirrored verbatim (../../schemas/widget-templates/branch-variant.ts):
//   sourceOptionSetWidgetId: string
//   variants: ReadonlyArray<BranchVariant>  — { id, label, attachToOptionIds[], overrideFields }
//
// Walker output: kind:'group' root with two children — sourceOptionSetWidgetId leaf +
// variants array. The per-variant group emits id/label leaves, an attachToOptionIds
// string array, and a kind:'group' overrideFields node with NO children. overrideFields
// is `Readonly<Record<string, unknown>>` — its value-shape is recipe-declared at the
// `recipe.variantOverrideShapes` layer (Plan 06 recipe-root walk) and is intentionally
// opaque at the per-template walker level (see widget-templates/branch-variant.ts D-11).

import type { FieldDescriptor } from "../descriptors.js";

export function walkBranchVariantConfig(configPath: string): FieldDescriptor {
  return {
    kind: "group",
    name: "config",
    path: configPath,
    children: [
      {
        kind: "leaf",
        name: "sourceOptionSetWidgetId",
        path: `${configPath}.sourceOptionSetWidgetId`,
        type: "string",
        required: true,
      },
      {
        kind: "array",
        name: "variants",
        path: `${configPath}.variants`,
        itemDescriptor: {
          kind: "group",
          name: "variant",
          path: `${configPath}.variants.*`,
          children: [
            {
              kind: "leaf",
              name: "id",
              path: `${configPath}.variants.*.id`,
              type: "string",
              required: true,
            },
            {
              kind: "leaf",
              name: "label",
              path: `${configPath}.variants.*.label`,
              type: "string",
              required: true,
            },
            {
              kind: "array",
              name: "attachToOptionIds",
              path: `${configPath}.variants.*.attachToOptionIds`,
              itemDescriptor: {
                kind: "leaf",
                name: "optionId",
                path: `${configPath}.variants.*.attachToOptionIds.*`,
                type: "string",
                required: true,
              },
            },
            {
              // overrideFields is Readonly<Record<string, unknown>> — opaque
              // at the per-template walker level. The recipe-root walk binds
              // `recipe.variantOverrideShapes` later (Plan 06 territory).
              kind: "group",
              name: "overrideFields",
              path: `${configPath}.variants.*.overrideFields`,
              children: [],
            },
          ],
        },
      },
    ],
  };
}
